import {
	Alert,
	Avatar,
	Button,
	Checkbox,
	Divider,
	FormControlLabel,
	Grid,
	Paper,
	Stack,
	type StandardTextFieldProps,
	TextField,
	Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { matchIsValidTel } from "mui-tel-input";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { apiHooks, type Schemas } from "../../lib/apis/norskGassnettApiHooks";
import LoadingButton from "../../lib/components/atomics/LoadingButton";
import MUIIcon from "../../lib/components/atomics/MUIIcon";
import MUITelInput from "../../lib/components/atomics/MUITelInput";
import SubpageWrapper from "../../lib/components/atomics/SubpageWrapper";
import CollapsableAlert from "../../lib/components/complex/CollapsableAlert";
import DatePicker from "../../lib/components/complex/DatePicker";
import TranslatedLink from "../../lib/components/complex/TranslatedLink";
import { EMAIL_REGEX } from "../../lib/helpers/regex";
import { getBorderRadius } from "../../lib/helpers/utils";
import { PATH as PATH_ROOT } from "../root";

export const PATH_SEGMENT = `flytteskjema`;
export const PATH = `/${PATH_SEGMENT}`;
export const getPath = () => PATH;

type RelocateFormSchema = {
	meterReading: string;
	relocationDate: string;
	invoiceAddress: string;
	postalCode: string;
	place: string;
	newOwnerName: string;
	newOwnerBirthdate: string;
	newOwnerTlf: string;
	newOwnerEmail: string;
	newOwnerAddress?: string;
	newOwnerPostalCode?: string;
	newOwnerPlace?: string;
	newOwnerIsAware: boolean;
};

interface SectionProps {
	index: number;
	title: string;
	required?: boolean;
	info?: string;
	children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({
	index,
	title,
	required,
	info,
	children,
}) => {
	const { t } = useTranslation();
	const color = index === 1 ? "primary" : "secondary";
	const borderPadding = "40px";
	return (
		<Paper sx={{ width: 1, p: 0, borderRadius: getBorderRadius }}>
			<Stack
				sx={{ width: 1, p: borderPadding, pb: 0 }}
				direction={{ xs: "column", md: "row" }}
				alignItems="center"
				justifyContent="space-between"
			>
				<Stack
					direction="row"
					alignItems="center"
					gap={2}
					sx={{ placeSelf: "start" }}
				>
					<Avatar
						sx={{
							color: `${color}.contrastText`,
							backgroundColor: `${color}.main`,
						}}
					>
						{index}
					</Avatar>
					<Typography variant="h3">{title}</Typography>
				</Stack>
				{required && (
					<Typography sx={{ fontStyle: "italic" }}>
						{t("relocate.required_field")}
					</Typography>
				)}
			</Stack>
			<Typography
				textAlign={{ xs: "center", md: "left" }}
				variant="body1"
				mx={borderPadding}
				mt={2}
				mb={3}
			>
				{info}
			</Typography>
			<Divider />
			<Grid sx={{ p: borderPadding }} container spacing={3}>
				{children}
			</Grid>
		</Paper>
	);
};

interface SectionInputProps extends Omit<StandardTextFieldProps, "type"> {
	name: string;
	type?: React.ReactNode;
	sm?: number;
	md?: number;
	required?: boolean;
	info?: string;
}

const SectionInput: React.FC<SectionInputProps> = ({
	name,
	type,
	sm,
	md,
	required = true,
	info,
	size,
	...props
}) => {
	const { t } = useTranslation();
	return (
		<Grid size={{ xs: 12, sm, md }}>
			<Stack direction="row" justifyContent="space-between">
				<Typography>
					{t(`relocate.${name}`)}
					{required && (
						<Typography component="span" color="error">
							{" "}
							*
						</Typography>
					)}
				</Typography>
				{info && (
					<Typography sx={{ display: { xs: "none", md: "block" } }}>
						{info}
					</Typography>
				)}
			</Stack>
			{type ?? <TextField type="text" name={name} fullWidth {...props} />}
			{info && <Typography sx={{ display: { md: "none" } }}>{info}</Typography>}
		</Grid>
	);
};

interface ClearFormProps {
	onClick: () => void;
	disabled?: boolean;
}

const ClearForm: React.FC<ClearFormProps> = ({ onClick, disabled }) => {
	const { t } = useTranslation();
	return (
		<Button
			onClick={onClick}
			variant="text"
			disabled={disabled}
			startIcon={<MUIIcon name="delete_forever" color="error" filled />}
		>
			{t("relocate.empty_form")}
		</Button>
	);
};

const isValidDate = (s?: string | null) => {
	if (!s) return false;
	const [valid, d, m, y] = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/) ?? [];
	if (!valid) return false;
	if (new Date(`${y}-${m}-${d}`).toString() === "Invalid Date") return false;
	return true;
};

const getEmptyRelocateFormData = (): RelocateFormSchema => ({
	meterReading: "",
	relocationDate: "",
	invoiceAddress: "",
	postalCode: "",
	place: "",
	newOwnerName: "",
	newOwnerBirthdate: "",
	newOwnerTlf: "",
	newOwnerEmail: "",
	newOwnerAddress: "",
	newOwnerPostalCode: "",
	newOwnerPlace: "",
	newOwnerIsAware: false,
});

const getEmptyRelocateFormErrors = (): Record<
	keyof RelocateFormSchema,
	string | null
> => ({
	meterReading: null,
	relocationDate: null,
	invoiceAddress: null,
	postalCode: null,
	place: null,
	newOwnerName: null,
	newOwnerBirthdate: null,
	newOwnerTlf: null,
	newOwnerEmail: null,
	newOwnerAddress: null,
	newOwnerPostalCode: null,
	newOwnerPlace: null,
	newOwnerIsAware: null,
});

const MeterReadingSchema = z.coerce.number().min(0);

const Relocate = () => {
	const { t } = useTranslation();

	const [relocateFormData, setRelocateFormData] = useState<RelocateFormSchema>(
		getEmptyRelocateFormData,
	);

	const [relocateFormErrors, setRelocateFormErrors] = useState<
		Record<keyof RelocateFormSchema, string | null>
	>(getEmptyRelocateFormErrors);

	const canSubmit =
		!Object.values(relocateFormErrors).some(Boolean) &&
		relocateFormData.newOwnerIsAware;

	const relocateCustomer = apiHooks.useMutation(
		"post",
		"/v1/customer/relocate",
	);

	const clearForm = () => {
		console.log("asdf");
		setRelocateFormData(getEmptyRelocateFormData());
	};

	const handleSubmit = async () => {
		if (!canSubmit) {
			console.error("Form cannot be submitted");
			return;
		}

		const { meterReading, ...rest } = relocateFormData;

		const parsedMeterReading = MeterReadingSchema.parse(meterReading);
		const body = {
			meterReading: parsedMeterReading,
			...rest,
		} as Schemas["RelocateCustomerRequestBody"];

		await relocateCustomer.mutateAsync({
			body: body,
		});
	};

	console.log(relocateFormData);
	console.log(relocateFormErrors);

	return (
		<SubpageWrapper
			title={t("relocate.title")}
			backRef={PATH_ROOT}
			specialAction={
				!relocateCustomer.isSuccess && (
					<ClearForm
						onClick={clearForm}
						disabled={relocateCustomer.isPending}
					/>
				)
			}
		>
			{relocateCustomer.isSuccess ? (
				<Paper sx={{ width: 1, p: 3, borderRadius: getBorderRadius }}>
					<Alert>
						<TranslatedLink
							linkProps={{ underline: "always" }}
							href={PATH_ROOT}
							content="relocate.success"
						/>
					</Alert>
				</Paper>
			) : (
				<Stack gap={3}>
					<Section
						index={1}
						title={t("relocate.your_info")}
						info={t("relocate.meter_reading_info")}
						required
					>
						<SectionInput
							name="meter_reading"
							required={true}
							sm={12}
							md={6}
							type={
								<TextField
									type="number"
									name="meter_reading"
									fullWidth
									value={relocateFormData.meterReading}
									error={!!relocateFormErrors.meterReading}
									onChange={(e) => {
										const isValid = z.coerce
											.number()
											.min(0)
											.safeParse(e.target.value).success;
										setRelocateFormErrors((s) => ({
											...s,
											meterReading: isValid
												? null
												: "relocate.error.meter_reading",
										}));
										setRelocateFormData((s) => ({
											...s,
											meterReading: e.target.value,
										}));
									}}
								/>
							}
						/>
						<SectionInput
							name="relocation_date"
							required={true}
							type={
								<DatePicker
									name="relocation_date"
									error={!!relocateFormErrors.relocationDate}
									required
									value={dayjs(relocateFormData.relocationDate)}
									onChange={(newDate) => {
										const date = newDate?.format("DD.MM.YYYY") || null;
										const isValid = isValidDate(date);
										setRelocateFormErrors((s) => ({
											...s,
											relocationDate: isValid
												? null
												: "relocate.error.relocation_date",
										}));
										setRelocateFormData((s) => ({
											...s,
											relocationDate: date,
										}));
									}}
									fullWidth
								/>
							}
							sm={12}
							md={6}
						/>
						<SectionInput
							name="invoice_address"
							autoComplete="street-address"
							required={true}
							error={!!relocateFormErrors.invoiceAddress}
							value={relocateFormData.invoiceAddress}
							onChange={(e) => {
								const value = e.target.value;
								const isValid = e.target.required ? !!value : true;
								setRelocateFormErrors((s) => ({
									...s,
									invoiceAddress: isValid
										? null
										: "relocate.error.invoice_address",
								}));
								setRelocateFormData((s) => ({
									...s,
									invoiceAddress: value,
								}));
							}}
						/>
						<SectionInput
							name="postal_code"
							autoComplete="postal-code"
							required={true}
							error={!!relocateFormErrors.postalCode}
							value={relocateFormData.postalCode}
							onChange={(e) => {
								const value = e.target.value;
								const isValid = e.target.required ? !!value : true;
								setRelocateFormErrors((s) => ({
									...s,
									postalCode: isValid ? null : "relocate.error.postal_code",
								}));
								setRelocateFormData((s) => ({
									...s,
									postalCode: value,
								}));
							}}
							sm={12}
							md={6}
						/>
						<SectionInput
							name="place"
							autoComplete="address-level2"
							required={true}
							error={!!relocateFormErrors.place}
							value={relocateFormData.place}
							onChange={(e) => {
								const value = e.target.value;
								const isValid = e.target.required ? !!value : true;
								setRelocateFormErrors((s) => ({
									...s,
									place: isValid ? null : "relocate.error.place",
								}));
								setRelocateFormData((s) => ({
									...s,
									place: value,
								}));
							}}
							sm={12}
							md={6}
						/>
					</Section>
					<Section
						index={2}
						title={t("relocate.new_owner_info")}
						info={t("relocate.missing_owner_info")}
					>
						<SectionInput
							name="new_owner_name"
							required={true}
							error={!!relocateFormErrors.newOwnerName}
							value={relocateFormData.newOwnerName}
							onChange={(e) => {
								const value = e.target.value;
								const isValid = e.target.required ? !!value : true;
								setRelocateFormErrors((s) => ({
									...s,
									newOwnerName: isValid
										? null
										: "relocate.error.new_owner_name",
								}));
								setRelocateFormData((s) => ({
									...s,
									newOwnerName: value,
								}));
							}}
							sm={12}
							md={6}
						/>
						<SectionInput
							name="new_owner_birthdate"
							required={true}
							type={
								<DatePicker
									name="new_owner_birthdate"
									error={!!relocateFormErrors.newOwnerBirthdate}
									required
									value={dayjs(relocateFormData.newOwnerBirthdate)}
									onChange={(newDate) => {
										const date = newDate?.format("DD.MM.YYYY") || null;
										const isValid = isValidDate(date);
										setRelocateFormErrors((s) => ({
											...s,
											newOwnerBirthdate: isValid
												? null
												: "relocate.error.new_owner_birthdate",
										}));
										setRelocateFormData((s) => ({
											...s,
											newOwnerBirthdate: date,
										}));
									}}
									fullWidth
								/>
							}
							sm={12}
							md={6}
						/>
						<SectionInput
							name="new_owner_tlf"
							type={
								<MUITelInput
									name="new_owner_tlf"
									error={!!relocateFormErrors.newOwnerTlf}
									value={relocateFormData.newOwnerTlf ?? undefined}
									onChange={(newTlf) => {
										const isValid = matchIsValidTel(newTlf);
										setRelocateFormErrors((s) => ({
											...s,
											newOwnerTlf: isValid
												? null
												: "relocate.error.new_owner_tlf",
										}));
										setRelocateFormData((s) => ({
											...s,
											newOwnerTlf: newTlf,
										}));
									}}
									fullWidth
								/>
							}
							md={5}
						/>
						<SectionInput
							name="new_owner_email"
							required={true}
							error={!!relocateFormErrors.newOwnerEmail}
							value={relocateFormData.newOwnerEmail}
							onChange={(e) => {
								const value = e.target.value;
								const isValid = value && EMAIL_REGEX.test(value);
								setRelocateFormErrors((s) => ({
									...s,
									newOwnerEmail: isValid
										? null
										: "relocate.error.new_owner_email",
								}));
								setRelocateFormData((s) => ({
									...s,
									newOwnerEmail: value,
								}));
							}}
							md={7}
						/>
						<SectionInput
							name="new_owner_address"
							required={false}
							error={!!relocateFormErrors.newOwnerAddress}
							value={relocateFormData.newOwnerAddress}
							onChange={(e) => {
								const value = e.target.value;
								const isValid = e.target.required ? !!value : true;
								setRelocateFormErrors((s) => ({
									...s,
									newOwnerAddress: isValid
										? null
										: "relocate.error.new_owner_address",
								}));
								setRelocateFormData((s) => ({
									...s,
									newOwnerAddress: value,
								}));
							}}
							info={t("relocate.new_owner_address_filled_if")}
						/>
						<SectionInput
							name="new_owner_postal_code"
							required={false}
							error={!!relocateFormErrors.newOwnerPostalCode}
							value={relocateFormData.newOwnerPostalCode}
							onChange={(e) => {
								const value = e.target.value;
								const isValid = e.target.required ? !!value : true;
								setRelocateFormErrors((s) => ({
									...s,
									newOwnerPostalCode: isValid
										? null
										: "relocate.error.new_owner_postal_code",
								}));
								setRelocateFormData((s) => ({
									...s,
									newOwnerPostalCode: value,
								}));
							}}
							sm={12}
							md={6}
						/>
						<SectionInput
							name="new_owner_place"
							required={false}
							error={!!relocateFormErrors.newOwnerPlace}
							value={relocateFormData.newOwnerPlace}
							onChange={(e) => {
								const value = e.target.value;
								const isValid = e.target.required ? !!value : true;
								setRelocateFormErrors((s) => ({
									...s,
									newOwnerPlace: isValid
										? null
										: "relocate.error.new_owner_place",
								}));
								setRelocateFormData((s) => ({
									...s,
									newOwnerPlace: value,
								}));
							}}
							sm={12}
							md={6}
						/>
						<Grid size={{ xs: 12 }}>
							<FormControlLabel
								disabled={relocateCustomer.isPending}
								checked={relocateFormData.newOwnerIsAware}
								onChange={(_, checked) => {
									setRelocateFormData((s) => ({
										...s,
										newOwnerIsAware: checked,
									}));
								}}
								control={<Checkbox />}
								label={t("relocate.new_owner_is_informed")}
							/>
						</Grid>
					</Section>
					<Stack my={2}>
						<CollapsableAlert
							alert={relocateCustomer.error && t("relocate.invalid_values")}
						/>
					</Stack>
					<Stack
						p={2}
						direction="row"
						justifyContent="space-between"
						alignItems="center"
					>
						<ClearForm
							onClick={clearForm}
							disabled={relocateCustomer.isPending}
						/>
						<LoadingButton
							type="button"
							onClick={() => handleSubmit()}
							endIcon={<MUIIcon name="arrow_forward" />}
							disabled={!canSubmit}
							loading={relocateCustomer.isPending}
						>
							{t("relocate.submit_form")}
						</LoadingButton>
					</Stack>
				</Stack>
			)}
		</SubpageWrapper>
	);
};

export default Relocate;
